import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, UserIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Trainer, useCreateTrainer, useGetTrainingTypes, useUpdateTrainer } from '@/api/trainers'
import { AvatarFormField, PhoneNumberFormField, SelectFormField } from '@/components/form'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { SelectOption } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard } from '@/hooks'
import { normalizeZipCode } from '@/pages/clients/client-profile/general-information/general-information.utils'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../shared/form-changes'
import { getPersonAvatarSrc } from '../../shared/person-avatar'
import { PersonFormLayout } from '../../shared/person-form-layout'
import { getPhoneDigits } from '../../shared/person-table.utils'
import { getTrainerServices } from '../staff.utils'
import {
  getGenderOptions,
  getTrainerFormDefaultValues,
  makeTrainerFormSchema,
  SERVICE_FEE_FORMATTERS,
  TRAINER_APARTMENT_MAX_LENGTH,
  TRAINER_NAME_MAX_LENGTH,
  TRAINER_STREET_MAX_LENGTH,
  TRAINER_ZIP_CODE_MAX_LENGTH,
  TrainerFormData,
  TrainerFormMode,
  TrainerServiceRow,
  toServicePayload,
} from './trainer-form-modal.constants'
import { TrainerServicesField } from './trainer-services-field'

interface TrainerFormModalProps {
  mode: TrainerFormMode
  onClose: VoidFunction
  trainer?: Trainer
}

export const TrainerFormModal = ({ mode, onClose, trainer }: TrainerFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { data: trainingTypes } = useGetTrainingTypes()
  const { mutateAsync: createTrainer } = useCreateTrainer()
  const { mutateAsync: updateTrainer } = useUpdateTrainer()

  const isEdit = mode === 'edit'
  const hadServices = !!trainer && getTrainerServices(trainer).length > 0

  const schema = useMemo(
    () => makeTrainerFormSchema(t, mode, { requireServices: isEdit && hadServices }),
    [t, mode, isEdit, hadServices],
  )

  // Note: the trainer's current services are kept even when their type is inactive - BE
  // replaces the whole list on save, so dropping them here would silently delete them
  const serviceOptions = useMemo<SelectOption<string>[]>(() => {
    const names = new Set<string>()
    ;(trainingTypes ?? []).forEach((type) => names.add(type.name))
    if (trainer) getTrainerServices(trainer).forEach((service) => names.add(service.serviceId.name))
    return [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }))
  }, [trainingTypes, trainer])

  const genderOptions = useMemo(() => getGenderOptions(t), [t])

  const existingAvatarUrl = isEdit && trainer ? getPersonAvatarSrc(trainer.user.avatar) : undefined

  const form = useForm<TrainerFormData>({
    defaultValues: getTrainerFormDefaultValues(trainer),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState

  const changeFields = useMemo<FormChangeFields<TrainerFormData>>(() => {
    const genderLabels = new Map<string, string>(
      genderOptions.map((option) => [option.value, option.label]),
    )

    return {
      avatar: { image: true, label: t('organisation:staff.form.avatar.label') },
      firstName: { label: t('organisation:staff.form.firstName.label') },
      lastName: { label: t('organisation:staff.form.lastName.label') },
      ...(isEdit ? {} : { email: { label: t('organisation:staff.form.email.label') } }),
      apartment: { label: t('organisation:staff.form.apartment.label') },
      gender: {
        format: (value) => genderLabels.get(String(value)) ?? EMPTY_VALUE,
        label: t('organisation:staff.form.gender.label'),
      },
      phone: { label: t('organisation:staff.form.phone.label') },
      services: {
        items: (value) =>
          (value as TrainerServiceRow[]).map(
            (service) => `${service.name} - ${SERVICE_FEE_FORMATTERS[service.type](service.fee)}`,
          ),
        label: t('organisation:staff.form.services.label'),
      },
      street: { label: t('organisation:staff.form.street.label') },
      zipCode: { label: t('organisation:staff.form.zipCode.label') },
      ...(isEdit
        ? {
            active: {
              format: (value) => (value ? t('common:booleans.yes') : t('common:booleans.no')),
              label: t('organisation:staff.form.active'),
            },
          }
        : {}),
    }
  }, [t, isEdit, genderOptions])

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<TrainerFormData> = async (data) => {
    const avatar = data.avatar?.[0]
    const phone = getPhoneDigits(data.phone)
    const firstName = data.firstName.trim()
    const lastName = data.lastName.trim()
    const services = toServicePayload(data.services)
    const address = {
      apartment: data.apartment?.trim() ?? '',
      street: data.street?.trim() ?? '',
      zipCode: normalizeZipCode(data.zipCode?.trim() ?? ''),
    }

    try {
      if (isEdit && trainer) {
        await updateTrainer({
          ...address,
          active: data.active,
          avatar,
          firstName,
          gender: data.gender,
          id: trainer.id,
          lastName,
          phone,
          services: services.length ? services : undefined,
        })
        toastFactory.success(t('organisation:staff.form.toasts.updated'))
      } else {
        const created = await createTrainer({
          avatar,
          email: data.email.trim(),
          firstName,
          gender: data.gender,
          lastName,
          phone,
          services,
        })

        // TODO: BE ignores street, apartment and zipCode on create, so they are saved with a follow-up update
        if (Object.values(address).some(Boolean)) {
          try {
            await updateTrainer({ ...address, id: created.id })
          } catch {
            toastFactory.error(t('organisation:staff.form.toasts.addressNotSaved'))
          }
        }
        toastFactory.success(t('organisation:staff.form.toasts.created'))
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit ? t('organisation:staff.form.titleEdit') : t('organisation:staff.form.titleNew')
      }
      headerIcon={UserIcon}
      footer={
        <>
          <Button use="secondary" onClick={handleClose}>
            {isEdit ? t('common:actions.cancel') : t('common:actions.back')}
          </Button>
          <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={existingAvatarUrl}
          label={t('organisation:staff.form.avatar.label')}
          uploadLabel={t('organisation:staff.form.avatar.uploadLabel')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:staff.form.firstName.label')}
            maxLength={TRAINER_NAME_MAX_LENGTH}
            name="firstName"
            placeholder={t('organisation:staff.form.firstName.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:staff.form.lastName.label')}
            maxLength={TRAINER_NAME_MAX_LENGTH}
            name="lastName"
            placeholder={t('organisation:staff.form.lastName.placeholder')}
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          required
          disabled={isEdit}
          label={t('organisation:staff.form.email.label')}
          name="email"
          placeholder={t('organisation:staff.form.email.placeholder')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<TrainerFormData, 'Male' | 'Female'>
            withReservedFooter
            required
            label={t('organisation:staff.form.gender.label')}
            name="gender"
            options={genderOptions}
            placeholder={t('organisation:staff.form.gender.placeholder')}
            size="medium"
            type="single"
          />
          <PhoneNumberFormField
            withReservedFooter
            required
            label={t('organisation:staff.form.phone.label')}
            name="phone"
            placeholder={t('organisation:staff.form.phone.placeholder')}
            size="medium"
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          label={t('organisation:staff.form.street.label')}
          maxLength={TRAINER_STREET_MAX_LENGTH}
          name="street"
          placeholder={t('organisation:staff.form.street.placeholder')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            label={t('organisation:staff.form.apartment.label')}
            maxLength={TRAINER_APARTMENT_MAX_LENGTH}
            name="apartment"
            placeholder={t('organisation:staff.form.apartment.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            label={t('organisation:staff.form.zipCode.label')}
            maxLength={TRAINER_ZIP_CODE_MAX_LENGTH}
            name="zipCode"
            placeholder={t('organisation:staff.form.zipCode.placeholder')}
          />
        </Grid>

        {isEdit && <SwitchFormField label={t('organisation:staff.form.active')} name="active" />}

        <TrainerServicesField
          keepAtLeastOne={isEdit && hadServices}
          serviceOptions={serviceOptions}
        />

        {!isEdit && (
          <Typography color="black500" size="text-small">
            {t('organisation:staff.form.setupHint')}
          </Typography>
        )}
      </PersonFormLayout>
    </Modal>
  )
}
